import { useAppState, type AppSnapshot } from '../../app/state';
import { callAction, ownerNodeOfDoc } from '../../app/actions';
import { ItemRow } from '../../components/ItemRow';
import { buildHiddenSets, canDocBtn, isPermHidden } from '../../lib/permissions';
import { docIconId, visibleDocsOf } from '../../lib/docs';
import type { HiddenSets } from '../../lib/permissions';
import type { DocId, NodeId } from '../../lib/types';
import { MyTodos } from './MyTodos';
import { SectionHeading } from './SectionHeading';

/**
 * "My items" — the sidebar pane of this user's favorites and recently-viewed documents
 * (plan 2.4).
 *
 * WHAT THE SERVER GAVE US IS REFERENCES, AND THIS IS WHERE THEY BECOME ROWS. `S.myItems`
 * holds bare `{targetType, targetId}` pairs; labels, colours, type icons and — critically —
 * VISIBILITY are all resolved here, at render time, from the same snapshot everything else
 * reads. That is the plan's filtering rule made structural: a favorite pointing at a node
 * this user may not see (`isPermHidden`, or stripped from `/api/map` entirely), or at a doc
 * whose slot their group's `docButtons` turns off (`visibleDocsOf`), simply fails to resolve
 * and is NOT rendered — and never deleted, so it reappears the day permissions return.
 *
 * SHARING THE SIDEBAR WITH THE TAG TREE IS THE TRANSITIONAL STATE, on purpose. Plan 2.3
 * (Track A) moves tags into a topbar dropdown; until it lands, this pane sits ABOVE the Tags
 * section and the tags markup, wiring and specs stay untouched. 2.3's removal of the Tags
 * section leaves this as the sidebar's only occupant with no rework here — the two
 * `.side-body` hosts split the height evenly today, and deleting one hands the other the
 * whole column for free.
 *
 * "MY ASSIGNMENTS" (V0.38) SITS FIRST, and renders NOTHING when there is nothing to show —
 * no title, no empty hint. Deliberately unlike the two sections below it: most users are
 * assigned nothing, the sidebar is in every view-mode pixel baseline, and a permanent new
 * heading would be a visible change nobody asked for. The section exists exactly when the
 * work does. Rows resolve like every other row here: a node that is hidden or gone, or a doc
 * this user's `docButtons` cannot see, simply fails to resolve and is not rendered — never
 * deleted, so it reappears the day permissions return. A team-borne row says "via {team}"
 * when the group directory can name the team, and shows without the label while it cannot
 * (the directory is participant-gated and loads beside this; blocking rows on it would hide
 * real work behind a lookup).
 */

type ResolvedDoc = { docId: DocId; title: string; ownerId: NodeId; ownerLabel: string };

/**
 * A doc reference this user can actually see right now, or null.
 *
 * The gate is the same pair every doc surface applies: the owning node must be visible
 * (present in the payload and not permission-hidden), and the doc must be one of the owner's
 * `visibleDocsOf` entries — which folds in the group's per-slot `docButtons`.
 */
function resolveDoc(s: AppSnapshot, hidden: HiddenSets, docId: DocId): ResolvedDoc | null {
  const ownerId = ownerNodeOfDoc(docId);
  if (!ownerId) return null; // detached, deleted, or on a branch the server stripped
  const owner = s.nodes[ownerId];
  if (!owner || isPermHidden(ownerId, s.edges, hidden)) return null;
  const gate = (perm: Parameters<typeof canDocBtn>[1]) => canDocBtn(s.perm, perm);
  if (!visibleDocsOf(owner, gate).some((d) => d.docId === docId)) return null;
  return {
    docId,
    title: s.docTitles[docId] || 'Untitled',
    ownerId,
    ownerLabel: owner.label || '',
  };
}

function DocTypeIcon({ s, docId }: { s: AppSnapshot; docId: DocId }) {
  return (
    <svg className="ico">
      <use href={`#${docIconId(s.docTypes, docId)}`} />
    </svg>
  );
}

const openDocRow = (d: ResolvedDoc) =>
  callAction(
    'openDocInCurrentMode',
    d.docId,
    (d.ownerLabel ? d.ownerLabel + ' — ' : '') + d.title,
    d.ownerId,
  );

export function MyItems() {
  const s = useAppState();
  if (!s.currentUser) return null;

  const hidden = buildHiddenSets(s.perm, s.tagEdges);

  /* ---- my assignments (V0.38): what I am responsible for, nodes then docs ---- */
  const teamName = (via: string | null): string | null =>
    via ? (s.groupDirectory.find((g) => g.id === via)?.name ?? null) : null;

  const assignmentRows = [
    ...s.myAssignments.nodes.map((a) => {
      const n = s.nodes[a.nodeId];
      if (!n || isPermHidden(a.nodeId, s.edges, hidden)) return null;
      const via = teamName(a.via);
      return (
        <ItemRow
          key={`anode:${a.nodeId}`}
          data={{ 'data-assign': `node:${a.nodeId}` }}
          icon={<span className="dot" style={{ background: n.color || 'var(--accent)' }} />}
          label={n.label || 'Untitled'}
          title={via ? `${n.label || 'Untitled'} · assigned via ${via}` : `${n.label || 'Untitled'} · assigned to you`}
          // `revealNodeOnMap` rather than centre-then-open (V0.42.1): `runCenter` filters
          // hidden nodes and returns silently, so an assignment under a folded parent used to
          // centre nothing at all. Revealing the branch first is what makes the camera land.
          onClick={() => callAction('revealNodeOnMap', a.nodeId)}
          trailing={
            <>
              {via ? <span className="myitems-via">via {via}</span> : null}
              <LocateEye nodeId={a.nodeId} />
            </>
          }
        />
      );
    }),
    ...s.myAssignments.docs.map((a) => {
      const d = resolveDoc(s, hidden, a.docPageId);
      if (!d) return null;
      const via = teamName(a.via);
      const meta = `${a.locale.toUpperCase()}${via ? ` · via ${via}` : ''}`;
      return (
        <ItemRow
          key={`adoc:${a.docPageId}:${a.locale}`}
          data={{ 'data-assign': `doc:${a.docPageId}:${a.locale}` }}
          icon={<DocTypeIcon s={s} docId={a.docPageId} />}
          label={d.title}
          title={`${d.title}${d.ownerLabel ? ` · ${d.ownerLabel}` : ''} · ${meta}`}
          onClick={() => openDocRow(d)}
          trailing={
            <>
              <span className="myitems-via">{meta}</span>
              {/* Locate the page's OWNING node — a document has no place on the map of its own. */}
              <LocateEye nodeId={d.ownerId} />
            </>
          }
        />
      );
    }),
  ].filter(Boolean);

  /* ---- favorites: nodes and docs, in the order they were starred ---- */
  const favRows = s.myItems.favorites
    .map((f) => {
      if (f.targetType === 'node') {
        const n = s.nodes[f.targetId];
        if (!n || isPermHidden(f.targetId, s.edges, hidden)) return null;
        return (
          <ItemRow
            key={`node:${f.targetId}`}
            data={{ 'data-fav': `node:${f.targetId}` }}
            icon={<span className="dot" style={{ background: n.color || 'var(--accent)' }} />}
            label={n.label || 'Untitled'}
            onClick={() => callAction('focusPopup', f.targetId)}
            trailing={<Unstar targetType="node" targetId={f.targetId} />}
          />
        );
      }
      const d = resolveDoc(s, hidden, f.targetId);
      if (!d) return null;
      return (
        <ItemRow
          key={`doc:${f.targetId}`}
          data={{ 'data-fav': `doc:${f.targetId}` }}
          icon={<DocTypeIcon s={s} docId={f.targetId} />}
          label={d.title}
          title={d.ownerLabel ? `${d.title} · ${d.ownerLabel}` : d.title}
          onClick={() => openDocRow(d)}
          trailing={<Unstar targetType="doc" targetId={f.targetId} />}
        />
      );
    })
    .filter(Boolean);

  /* ---- recently viewed: docs only, newest first (the server's order, kept) ---- */
  const recentRows = s.myItems.recents
    .map((r) => {
      const d = resolveDoc(s, hidden, r.targetId);
      if (!d) return null;
      return (
        <ItemRow
          key={r.targetId}
          data={{ 'data-recent': r.targetId }}
          icon={<DocTypeIcon s={s} docId={r.targetId} />}
          label={d.title}
          title={d.ownerLabel ? `${d.title} · ${d.ownerLabel}` : d.title}
          onClick={() => openDocRow(d)}
        />
      );
    })
    .filter(Boolean);

  // Hints only once the list has actually been READ: before that, "empty" is not a fact yet,
  // and a hint that flashes in and out on every boot is noise.
  const loaded = s.myItems.loaded;

  /* EVERY HEADING FOLDS SINCE V0.50 (`SectionHeading`), because the pane has five sections now
     and four unfoldable lists is where a sidebar stops being usable. Per user and LOCAL —
     pointedly not the tag panel's shared fold, which CLAUDE.md names a mistake not to repeat. */
  const folded = (key: string) => s.collapsedSections.has(key);

  return (
    <>
      {/* MY TO-DOS sits first: it is the only section that is a list of things still to DO, and
          the three below it are references to things that already exist. It draws its own
          heading and self-gates on VIEW_TODOS, so a reader with no to-do feature sees the pane
          exactly as it was. */}
      <MyTodos s={s} hidden={hidden} />
      {assignmentRows.length ? (
        <>
          <SectionHeading
            sectionKey="myitems:assignments"
            label="My assignments"
            s={s}
            trailing={
              /* The eye reveals my assignments on the map — highlights the nodes carrying them,
                 dims the rest, and expands collapsed ancestors (V0.42). A transient view toggle. */
              <button
                type="button"
                className={`myitems-eye${s.eyeOn ? ' on' : ''}`}
                id="myitems-eye"
                aria-pressed={s.eyeOn}
                title={s.eyeOn ? 'Stop highlighting my assignments on the map' : 'Highlight my assignments on the map'}
                onClick={() => callAction('toggleEyeAssignments')}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <use href={s.eyeOn ? '#i-eye' : '#i-eye-off'} />
                </svg>
              </button>
            }
          />
          {folded('myitems:assignments') ? null : assignmentRows}
        </>
      ) : null}
      <SectionHeading sectionKey="myitems:favorites" label="Favorites" s={s} />
      {folded('myitems:favorites') ? null : favRows.length ? (
        favRows
      ) : loaded ? (
        <div className="myitems-empty">Nothing starred yet — the star on a node or document adds it here.</div>
      ) : null}
      <SectionHeading sectionKey="myitems:recents" label="Recently viewed" s={s} />
      {folded('myitems:recents') ? null : recentRows.length ? (
        recentRows
      ) : loaded ? (
        <div className="myitems-empty">Documents you open appear here.</div>
      ) : null}
    </>
  );
}

/**
 * Locate ONE assignment on the map (V0.42.1) — the per-row twin of the section's eye.
 *
 * The section eye FILTERS (highlight-and-dim, transient, cleared when you turn it off); this is
 * NAVIGATION, so it uses the permanent reveal instead: expand the branch, open the node, centre
 * the camera. `stopPropagation` like the un-star control below, or the row's own click fires too.
 */
function LocateEye({ nodeId }: { nodeId: string }) {
  return (
    <span
      className="row-locate"
      title="Show this on the map"
      onClick={(e) => {
        e.stopPropagation();
        callAction('revealNodeOnMap', nodeId);
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <use href="#i-eye" />
      </svg>
    </span>
  );
}

/** The row's un-star control. A span, as the tag rows' controls are, revealed on hover. */
function Unstar({ targetType, targetId }: { targetType: 'node' | 'doc'; targetId: string }) {
  return (
    <span
      className="row-x"
      title="Remove from favorites"
      onClick={(e) => {
        e.stopPropagation();
        callAction('toggleFavorite', targetType, targetId);
      }}
    >
      ×
    </span>
  );
}
